import { useEffect, useState, useCallback } from "react";
import { api } from "@/api/client";
import { fmtDate } from "@/components/common";
import { Loader2, ArrowDownToLine, ArrowUpFromLine, Search, X, MessageSquare } from "lucide-react";

export default function MobileWhatsApp() {
  const [dir, setDir] = useState("incoming");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (search.trim()) params.search = search.trim();
      const r = await api.get(`/whatsapp/messages/${dir}`, { params });
      setRows(r.data?.data || []);
      setMeta(r.data?.meta || {});
    } catch {} finally { setLoading(false); }
  }, [dir, page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [dir, search]);

  return (
    <div className="flex flex-col h-full">
      {/* Tab + search */}
      <div className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur px-4 pt-3 pb-2 space-y-2">
        <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
          {["incoming", "outgoing"].map(d => (
            <button key={d} onClick={() => setDir(d)}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                dir === d ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
              }`}>
              {d === "incoming" ? "Incoming" : "Outgoing"}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search number or message..."
            className="w-full pl-9 pr-8 py-2.5 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-[#334E7B] focus:ring-2 focus:ring-[#334E7B]/20" />
          {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><X size={13}/></button>}
        </div>
      </div>

      <div className="px-4 py-1 text-xs text-slate-400">{meta?.total ?? rows.length} messages</div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
        {loading ? (
          <div className="flex justify-center pt-8"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center pt-12">
            <MessageSquare size={32} className="mx-auto mb-2 text-slate-200" />
            <div className="text-sm text-slate-400">No messages found</div>
          </div>
        ) : (
          rows.map(m => (
            <div key={m.id} className="bg-white rounded-2xl border border-slate-100 p-3.5">
              <div className="flex items-start gap-2.5">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${dir === "incoming" ? "bg-blue-50" : "bg-emerald-50"}`}>
                  {dir === "incoming"
                    ? <ArrowDownToLine size={13} className="text-[#334E7B]" />
                    : <ArrowUpFromLine size={13} className="text-emerald-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-mono text-xs text-slate-700 font-semibold">
                      {dir === "incoming" ? m.from_number : m.to_number}
                    </span>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                      m.status === "sent" ? "bg-emerald-50 text-emerald-700" :
                      m.status === "failed" ? "bg-red-50 text-red-700" :
                      "bg-slate-50 text-slate-500"
                    }`}>{m.status || "—"}</span>
                  </div>
                  <div className="text-xs text-slate-600 line-clamp-2 font-mono">{m.body}</div>
                  {dir === "incoming" && m.parsed_command?.intent && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="badge-base bg-violet-50 text-violet-700 border-violet-200">{m.parsed_command.intent}</span>
                      {m.parsed_command?.ai_assisted && <span className="badge-base bg-blue-50 text-blue-700 border-blue-200">AI</span>}
                    </div>
                  )}
                  <div className="text-[10px] text-slate-400 mt-1.5">{fmtDate(m.created_at)}</div>
                </div>
              </div>
            </div>
          ))
        )}

        {meta?.total_pages > 1 && (
          <div className="flex gap-2 pt-1">
            <button disabled={page <= 1} onClick={() => setPage(p => p-1)}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm disabled:opacity-40 bg-white font-medium">Prev</button>
            <span className="flex items-center text-xs text-slate-400 px-2">{page}/{meta.total_pages}</span>
            <button disabled={page >= meta.total_pages} onClick={() => setPage(p => p+1)}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm disabled:opacity-40 bg-white font-medium">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
